import { type ReactNode, useRef, useState } from 'react';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '@/app/components/ui/hover-card';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, getLoginUrl, reportEvent } from '@/app/utils';
import { openInNewTab, shouldOpenInNewTab } from '@/app/utils/client';
import { usePathname, useRouter } from '@/i18n/routing';
import { isAuthRequired } from '../_utils/is-auth-required';
import type { SingleMenuItem } from '../types';

export const MenuItem = ({
  name,
  icon,
  path,
  activePaths,
  className,
  onClick,
  badge,
  tag,
  exact = false,
  id,
  collapsed = false,
  shortName,
  reportElementName,
  subMenuItems,
}: {
  name: string;
  icon?: ReactNode;
  path?: string;
  activePaths?: string[];
  badge?: ReactNode;
  className?: string;
  onClick?: () => void;
  tag?: string;
  exact?: boolean;
  id?: string;
  collapsed?: boolean;
  shortName?: string;
  reportElementName?: string;
  subMenuItems?: SingleMenuItem[];
}) => {
  const pathname = usePathname();
  const router = useRouter();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const userInfo = useStore((state) => state.userInfo);

  const matchesPath = (targetPath: string) => pathname === targetPath || pathname.startsWith(`${targetPath}/`);

  const isActive = () => {
    const pathsToMatch = [path, ...(activePaths ?? [])].filter((item): item is string => Boolean(item));
    if (pathsToMatch.length === 0) return false;
    if (exact) {
      return pathsToMatch.some((targetPath) => pathname === targetPath);
    }
    return pathsToMatch.some((targetPath) => matchesPath(targetPath));
  };
  const active = isActive();
  const _hasSubMenu = Boolean(subMenuItems?.length);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [clickOpen, setClickOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleMenuClick = (href: string, shouldOpenNewTab = false, elementName = reportElementName) => {
    const menuItemName = elementName ?? href.split('/').slice(-1)[0];
    const isNeedLogin = isAuthRequired(href);
    const targetHref = userInfo || !isNeedLogin ? href : getLoginUrl(href);
    if (menuItemName === 'templates') {
      reportEvent('templates_exposure', {
        refer_element: 'menu - templates',
      });
    } else {
      reportEvent('element_click', {
        element_name: `menu - ${menuItemName}`,
      });
    }
    if (shouldOpenNewTab) {
      openInNewTab(targetHref);
      if (!userInfo && isNeedLogin) {
        reportEvent('login_page_exposure', {
          refer_element: `menu - ${menuItemName}`,
          refer_page: pathname,
        });
      }
      return;
    }
    if (userInfo || !isNeedLogin) {
      router.push(targetHref);
    } else {
      openLoginDialog({ redirectUrl: href });
      reportEvent('login_pop_exposure', {
        refer_element: `menu - ${menuItemName}`,
        refer_page: pathname,
      });
    }
  };

  const content = (
    <div
      key={name}
      ref={triggerRef}
      className={cn(
        'relative flex h-11 select-none items-center rounded-10 px-3 text-system-white/40 transition-colors',
        collapsed && 'h-[52px] flex-col justify-center gap-0.5 px-0',
        active ? 'bg-system-white16 text-system-white' : 'cursor-pointer hover:bg-system-white08',
        className,
      )}
      onClick={(e) => {
        onClick?.();
        if (!path) return;
        handleMenuClick(path, shouldOpenInNewTab(e));
      }}
      id={id}
    >
      <div className="flex-shrink-0">{icon}</div>

      {!collapsed && (
        <div className={cn('flex flex-1 items-center', badge && !tag && 'pr-8')}>
          <span className="mr-2 ml-3 whitespace-pre-line font-medium">{name}</span>
          {tag ? (
            <div className="flex-1 overflow-hidden">
              <img src={tag} alt="tag" className="max-h-[22px] min-h-4 object-contain" />
            </div>
          ) : null}
        </div>
      )}
      {collapsed && (
        <span
          className={cn(
            'w-full whitespace-nowrap text-center font-medium text-[10px]',
            active ? 'text-system-white' : 'text-system-white/40',
          )}
        >
          {shortName || name}
        </span>
      )}
      {badge && !tag && (
        <div className={cn('pointer-events-none absolute z-[1]', collapsed ? 'top-1 right-1' : 'top-[7px] right-3')}>
          {badge}
        </div>
      )}
    </div>
  );

  if (subMenuItems?.length) {
    return (
      <HoverCard openDelay={100} closeDelay={100} open={hoverOpen || clickOpen} onOpenChange={setHoverOpen}>
        <HoverCardTrigger asChild>{content}</HoverCardTrigger>
        <HoverCardPortal>
          <HoverCardContent
            side="right"
            align="center"
            sideOffset={8}
            onEscapeKeyDown={() => setClickOpen(false)}
            onInteractOutside={(event) => {
              if (triggerRef.current?.contains(event.target as Node)) return;
              setClickOpen(false);
            }}
            className="flex w-fit flex-col rounded-12 border border-system-white08 bg-[rgba(34,37,42,0.64)] p-1 shadow-[0_4px_20px_0_rgba(0,0,0,0.4)] backdrop-blur-[30px]"
          >
            {subMenuItems.map((subItem) => (
              <button
                key={subItem.id ?? subItem.path ?? subItem.name}
                type="button"
                className="flex h-10 min-w-[120px] cursor-pointer items-center justify-center gap-3 rounded-8 px-2 transition-colors hover:bg-system-white08"
                onClick={(e) => {
                  setClickOpen(false);
                  subItem.onClick?.();
                  if (!subItem.path) return;
                  handleMenuClick(subItem.path, shouldOpenInNewTab(e), subItem.reportElementName);
                }}
              >
                {subItem.icon && <span className="flex shrink-0 items-center">{subItem.icon}</span>}
                <span className="whitespace-nowrap font-normal text-sm leading-[1.4]">{subItem.name}</span>
              </button>
            ))}
          </HoverCardContent>
        </HoverCardPortal>
      </HoverCard>
    );
  }

  return collapsed ? (
    <CustomTooltip content={name} side="right" sideOffset={8}>
      {content}
    </CustomTooltip>
  ) : (
    content
  );
};
